import { useMemo, useState } from "react";
import { useAppData } from "@/data/AppDataContext";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, PieChart, Pie,
} from "recharts";
import {
  FileText, Download, Mail, Eye, Users,
  Phone, PhoneOff, CalendarCheck, CalendarX, DollarSign, XCircle,
  CheckCircle, TrendingUp, Send, ArrowLeft, Tag,
} from "lucide-react";
import { Link } from "react-router-dom";
import { BackToLeadPerformance } from "@/components/BackToLeadPerformance";
import { toast } from "sonner";
import { calculateCloseRatePercent } from "@/lib/metricsUtils";
import { getRepPerformanceSummary, getSourcePerformanceSummary } from "@/data/appointmentResultSelectors";
import { DispositionService, DISPOSITION_REASONS, type DispositionReason } from "@/services/disposition.service";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";

/* ── Helpers ── */
function downloadCSV(filename: string, headers: string[], rows: (string | number)[][]) {
  const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${c}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface DispositionReportProps {
  /** When embedded inside a cockpit shell (Lead Performance tab or /disposition-report cockpit page),
   *  hide this component's own header + KPI grid since the parent shell already provides them. */
  embedded?: boolean;
}

// Cockpit palette: vivid, semantic, no grey on the black canvas.
// Hard misses lean rose/amber, soft/data-quality misses lean cyan/violet/indigo,
// keeping intent legible at a glance.
const REASON_COLORS: Record<DispositionReason, string> = {
  "Duplicate Lead":       "hsl(190 80% 55%)",  // cyan — data hygiene
  "Out of Service Area":  "hsl(220 80% 62%)",  // indigo — geographic
  "Unqualified Customer": "hsl(265 75% 65%)",  // violet — fit
  "Bad Contact Info":     "hsl(175 65% 50%)",  // teal — data hygiene
  "Price Sensitivity":    "hsl(38 92% 60%)",   // amber — recoverable miss
  "Competitor Won":       "hsl(350 80% 62%)",  // rose — hard loss
  "Other":                "hsl(295 60% 62%)",  // magenta — catch-all
};

const REASON_BG: Record<DispositionReason, string> = {
  "Duplicate Lead": "bg-muted text-muted-foreground",
  "Out of Service Area": "bg-muted text-muted-foreground",
  "Unqualified Customer": "bg-muted text-muted-foreground",
  "Bad Contact Info": "bg-muted text-muted-foreground",
  "Price Sensitivity": "bg-warning/10 text-warning",
  "Competitor Won": "bg-destructive/10 text-destructive",
  "Other": "bg-muted text-muted-foreground",
};

export default function DispositionReport({ embedded = false }: DispositionReportProps = {}) {
  const { jobs, issues, credits, appointmentResults, leadSources } = useAppData();
  const [sourceFilter, setSourceFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("2026-01-01");
  const [dateTo, setDateTo] = useState("2026-03-31");
  const [showPreview, setShowPreview] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("Lead Disposition Report");
  const [emailNotes, setEmailNotes] = useState("");

  // Core disposition data from service (uses leadSources + jobs/issues/credits)
  const dispositionData = useMemo(() => {
    return DispositionService.calculateDispositionData(
      leadSources,
      jobs,
      issues,
      credits,
      { dateFrom, dateTo }
    );
  }, [leadSources, jobs, issues, credits, dateFrom, dateTo]);

  // Apply source-type filter (dropdown)
  const filtered = useMemo(() => {
    return DispositionService.filterBySourceType(dispositionData, sourceFilter);
  }, [dispositionData, sourceFilter]);

  // Totals row
  const totals = useMemo(() => {
    return DispositionService.calculateTotals(filtered);
  }, [filtered]);

  // Rejection reason breakdown
  const reasonTotals = useMemo(() => {
    return DispositionService.calculateReasonTotals(filtered);
  }, [filtered]);

  // Source types for the dropdown (distinct list)
  const sourceTypes = useMemo(() => {
    return DispositionService.getSourceTypes(dispositionData);
  }, [dispositionData]);

  const totalRejections = Object.values(reasonTotals).reduce((s, v) => s + v, 0);

  const reasonPieData = DISPOSITION_REASONS
    .filter((r) => reasonTotals[r] > 0)
    .map((r) => ({ name: r, value: reasonTotals[r], fill: REASON_COLORS[r] }));

  const handleExport = () => {
    const reasonHeaders = DISPOSITION_REASONS.map((r) => `Rej: ${r}`);
    const rows = filtered.map((d) => [
      d.name, d.sourceType, d.totalLeads, d.contacted, d.noContact,
      d.appointmentsSet, d.appointmentsCompleted, d.noShow,
      d.sold, d.rejected, d.revenue, d.cost, d.conversionRate,
      ...DISPOSITION_REASONS.map((r) => d.rejectionReasons[r] || 0),
    ]);
    downloadCSV(`disposition-report-${dateFrom}-to-${dateTo}.csv`, [
      "Source", "Type", "Total Leads", "Contacted", "No Contact",
      "Appts Set", "Appts Completed", "No-Show",
      "Sold", "Rejected/Invalid", "Revenue", "Cost", "Conversion %",
      ...reasonHeaders,
    ], rows);
    toast.success("Disposition report exported with reason details.");
  };

  const handleSendEmail = () => {
    if (!emailTo.trim()) { toast.error("Recipient email is required."); return; }
    toast.success(`Disposition report queued to ${emailTo}`, {
      description: "Email notification placeholder — integration ready.",
    });
    setShowEmail(false);
    setEmailTo("");
    setEmailNotes("");
  };

  const chartData = filtered.map((d) => ({
    name: d.name.length > 12 ? d.name.slice(0, 10) + "…" : d.name,
    sold: d.sold,
    rejected: d.rejected,
    noShow: d.noShow,
    noContact: d.noContact,
  }));

  return (
    <div className={embedded ? "space-y-5" : "space-y-5 max-w-7xl"}>
      {/* Header — hidden when embedded; parent cockpit shell provides it */}
      {!embedded && (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <BackToLeadPerformance />
            <h1 className="text-lg font-bold tracking-tight flex items-center gap-2 mt-1.5">
              <FileText className="h-5 w-5 text-primary" />
              Aggregator Disposition Report
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Lead outcome accountability · {dateFrom} to {dateTo}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="text-xs h-8 gap-1.5" onClick={() => setShowPreview(true)}>
              <Eye className="h-3.5 w-3.5" /> Preview Report
            </Button>
            <Button variant="outline" size="sm" className="text-xs h-8 gap-1.5" onClick={handleExport}>
              <Download className="h-3.5 w-3.5" /> Export CSV
            </Button>
            <Button size="sm" className="text-xs h-8 gap-1.5" onClick={() => {
              setEmailSubject(`Lead Disposition Report — ${dateFrom} to ${dateTo}`);
              setShowEmail(true);
            }}>
              <Mail className="h-3.5 w-3.5" /> Email Report
            </Button>
          </div>
        </div>
      )}

      {/* Embedded actions row — compact, no title (cockpit masthead owns the title) */}
      {embedded && (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs text-muted-foreground">
            Lead outcome accountability · {dateFrom} to {dateTo}
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="text-xs h-8 gap-1.5" onClick={() => setShowPreview(true)}>
              <Eye className="h-3.5 w-3.5" /> Preview
            </Button>
            <Button variant="outline" size="sm" className="text-xs h-8 gap-1.5" onClick={handleExport}>
              <Download className="h-3.5 w-3.5" /> Export
            </Button>
            <Button variant="outline" size="sm" className="text-xs h-8 gap-1.5" onClick={() => {
              setEmailSubject(`Lead Disposition Report — ${dateFrom} to ${dateTo}`);
              setShowEmail(true);
            }}>
              <Mail className="h-3.5 w-3.5" /> Email
            </Button>
          </div>
        </div>
      )}

       {/* Filters */}
       <div className="flex flex-wrap items-end gap-3">
         <div className="space-y-1">
           <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em]">From</Label>
           <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs w-[150px]" />
         </div>
         <div className="space-y-1">
           <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em]">To</Label>
           <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs w-[150px]" />
         </div>
         <div className="space-y-1">
           <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em]">Source Type</Label>
           <Select value={sourceFilter} onValueChange={setSourceFilter}>
             <SelectTrigger className="h-8 text-xs w-[150px]"><SelectValue /></SelectTrigger>
             <SelectContent>
               <SelectItem value="all">All Sources</SelectItem>
               {sourceTypes.map((t) => <SelectItem key={t} value={t} className="text-xs capitalize">{t.replace("_", " ")}</SelectItem>)}
             </SelectContent>
           </Select>
         </div>
       </div>

      {/* KPI Summary — hairline cockpit cards (operational metrics beyond hero's Close Rate / Sold / No Demo) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-px bg-border/60 border border-border/60 rounded-sm overflow-hidden">
        {[
          { label: "Total Leads", value: totals.totalLeads, icon: Users },
          { label: "Contacted", value: totals.contacted, icon: Phone },
          { label: "No Contact", value: totals.noContact, icon: PhoneOff, tone: totals.noContact > 0 ? "warn" : undefined },
          { label: "Appts Set", value: totals.appointmentsSet, icon: CalendarCheck },
          { label: "No-Show", value: totals.noShow, icon: CalendarX, tone: totals.noShow > 0 ? "warn" : undefined },
          { label: "Rejected", value: totals.rejected, icon: XCircle, tone: totals.rejected > 0 ? "warn" : undefined },
          { label: "Revenue", value: `$${totals.revenue.toLocaleString()}`, icon: DollarSign, tone: totals.revenue > 0 ? "good" : undefined },
          { label: "Total Cost", value: totals.cost > 0 ? `$${totals.cost.toLocaleString()}` : "—", icon: TrendingUp },
          { label: "Conversion", value: totals.totalLeads > 0 ? `${Math.round((totals.sold / totals.totalLeads) * 100)}%` : "—", icon: TrendingUp, tone: "good" },
          { label: "Sold", value: totals.sold, icon: CheckCircle, tone: "good" },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-background px-3 py-2.5 text-center">
            <kpi.icon className="h-3.5 w-3.5 mx-auto mb-1 text-muted-foreground/60" />
            <p className={`text-base font-semibold leading-none tabular-nums ${kpi.tone === "good" ? "text-success" : kpi.tone === "warn" ? "text-warning" : "text-foreground"}`}>{kpi.value}</p>
            <p className="text-[8px] text-muted-foreground mt-1 font-medium uppercase tracking-wider">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Disposition Chart */}
      {chartData.length > 0 && (
        <div className="border border-border/60 rounded-sm bg-background p-4">
            <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-3">Lead Outcomes by Source</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-25} textAnchor="end" height={55} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 9 }} width={30} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ fontSize: 11, background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--popover-foreground))" }} />
                {/* Cockpit palette on black: emerald win, amber soft miss, rose hard miss, violet no-contact */}
                <Bar dataKey="sold"      fill="hsl(158 70% 52%)"  radius={[2, 2, 0, 0]} name="Sold"       stackId="a" />
                <Bar dataKey="rejected"  fill="hsl(350 80% 62%)"  radius={[0, 0, 0, 0]} name="Rejected"   stackId="a" />
                <Bar dataKey="noShow"    fill="hsl(38 92% 60%)"   radius={[0, 0, 0, 0]} name="No-Show"    stackId="a" />
                <Bar dataKey="noContact" fill="hsl(265 75% 65%)"  radius={[0, 0, 2, 2]} name="No Contact" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-4 justify-center mt-2">
              {[
                { label: "Sold",       color: "hsl(158 70% 52%)" },
                { label: "Rejected",   color: "hsl(350 80% 62%)" },
                { label: "No-Show",    color: "hsl(38 92% 60%)"  },
                { label: "No Contact", color: "hsl(265 75% 65%)" },
              ].map((l) => (
                <span key={l.label} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: l.color }} />
                  {l.label}
                </span>
              ))}
            </div>
          </div>
      )}

      {/* ═══ Disposition Reason Breakdown ═══ */}
      {totalRejections > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Reason Summary with pie */}
          <div className="border border-border/60 rounded-sm bg-background p-4">
              <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-3 flex items-center gap-1.5">
                <Tag className="h-3 w-3" /> Rejection Reasons Summary
              </p>
              <div className="flex items-start gap-4">
                <ResponsiveContainer width={120} height={120} className="shrink-0">
                  <PieChart>
                    <Pie data={reasonPieData} dataKey="value" cx="50%" cy="50%" innerRadius={30} outerRadius={55} paddingAngle={2} strokeWidth={0}>
                      {reasonPieData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 11, background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--popover-foreground))" }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-1.5">
                  {DISPOSITION_REASONS.filter((r) => reasonTotals[r] > 0).map((reason) => {
                    const count = reasonTotals[reason];
                    const pct = totalRejections > 0 ? Math.round((count / totalRejections) * 100) : 0;
                    return (
                      <div key={reason} className="flex items-center gap-2">
                        <span className="text-[11px] font-medium w-36 truncate">{reason}</span>
                        <div className="flex-1">
                          <Progress value={pct} className="h-1.5" />
                        </div>
                        <span className="text-[11px] font-bold w-8 text-right">{count}</span>
                        <span className="text-[10px] text-muted-foreground w-10 text-right">{pct}%</span>
                      </div>
                    );
                  })}
                  <div className="border-t pt-1 mt-1 flex items-center justify-between text-xs font-bold">
                    <span>Total Rejections</span>
                    <span>{totalRejections}</span>
                  </div>
                </div>
              </div>
            </div>

          {/* Per-source reason breakdown */}
          <div className="border border-border/60 rounded-sm bg-background p-4">
              <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-3 flex items-center gap-1.5">
                <XCircle className="h-3 w-3" /> Rejection Reasons by Source
              </p>
              <div className="space-y-3 max-h-[280px] overflow-y-auto">
                {filtered.filter((d) => d.rejected > 0).map((d) => {
                  const topReasons = DISPOSITION_REASONS
                    .filter((r) => d.rejectionReasons[r] > 0)
                    .sort((a, b) => d.rejectionReasons[b] - d.rejectionReasons[a]);
                  return (
                    <div key={d.name} className="rounded-lg border p-2.5 bg-muted/10">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-xs font-semibold">{d.name}</p>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-destructive/10 text-destructive border-destructive/20">
                          {d.rejected} rejected
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {topReasons.map((reason) => (
                          <Badge key={reason} variant="outline" className={`text-[9px] px-1.5 py-0 ${REASON_BG[reason]}`}>
                            {reason}: {d.rejectionReasons[reason]}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
        </div>
      )}

      {/* Disposition Table */}
      <div className="border border-border/60 rounded-sm bg-background">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px] font-semibold">Source</TableHead>
                  <TableHead className="text-[10px] font-semibold text-center">Leads</TableHead>
                  <TableHead className="text-[10px] font-semibold text-center">Contacted</TableHead>
                  <TableHead className="text-[10px] font-semibold text-center">No Contact</TableHead>
                  <TableHead className="text-[10px] font-semibold text-center hidden sm:table-cell">Appt Set</TableHead>
                  <TableHead className="text-[10px] font-semibold text-center hidden sm:table-cell">No-Show</TableHead>
                  <TableHead className="text-[10px] font-semibold text-center">Sold</TableHead>
                  <TableHead className="text-[10px] font-semibold text-center hidden md:table-cell">Rejected</TableHead>
                  <TableHead className="text-[10px] font-semibold text-center hidden lg:table-cell">Top Reason</TableHead>
                  <TableHead className="text-[10px] font-semibold text-right hidden sm:table-cell">Revenue</TableHead>
                  <TableHead className="text-[10px] font-semibold text-center hidden lg:table-cell">Conv%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((d) => {
                  const topReason = DISPOSITION_REASONS
                    .filter((r) => d.rejectionReasons[r] > 0)
                    .sort((a, b) => d.rejectionReasons[b] - d.rejectionReasons[a])[0];
                  return (
                    <TableRow key={d.name}>
                      <TableCell className="py-2">
                        <div>
                          <p className="text-xs font-semibold">{d.name}</p>
                          <Badge variant="outline" className="text-[9px] px-1 py-0 capitalize">{d.sourceType.replace("_", " ")}</Badge>
                        </div>
                      </TableCell>
                      <TableCell className="py-2 text-center text-xs font-medium tabular-nums">{d.totalLeads}</TableCell>
                      <TableCell className="py-2 text-center text-xs tabular-nums">{d.contacted}</TableCell>
                      <TableCell className={`py-2 text-center text-xs tabular-nums ${d.noContact > 0 ? "text-warning font-medium" : "text-muted-foreground/60"}`}>{d.noContact}</TableCell>
                      <TableCell className="py-2 text-center text-xs tabular-nums hidden sm:table-cell">{d.appointmentsSet}</TableCell>
                      <TableCell className={`py-2 text-center text-xs tabular-nums hidden sm:table-cell ${d.noShow > 0 ? "text-warning" : "text-muted-foreground/60"}`}>{d.noShow}</TableCell>
                      <TableCell className={`py-2 text-center text-xs tabular-nums ${d.sold > 0 ? "text-foreground font-semibold" : "text-muted-foreground/60"}`}>{d.sold}</TableCell>
                      <TableCell className={`py-2 text-center text-xs tabular-nums hidden md:table-cell ${d.rejected > 0 ? "text-warning" : "text-muted-foreground/60"}`}>{d.rejected}</TableCell>
                      <TableCell className="py-2 text-center hidden lg:table-cell">
                        {topReason ? (
                          <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${REASON_BG[topReason]}`}>
                            {topReason}
                          </Badge>
                        ) : <span className="text-[10px] text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="py-2 text-right text-xs font-medium hidden sm:table-cell">${d.revenue.toLocaleString()}</TableCell>
                      <TableCell className="py-2 text-center hidden lg:table-cell">
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 tabular-nums ${d.conversionRate >= 15 ? "border-success/40 text-success" : d.conversionRate >= 5 ? "border-border text-foreground" : "border-border text-muted-foreground"}`}>
                          {d.conversionRate}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className="bg-muted/30 font-bold">
                  <TableCell className="py-2 text-xs">TOTAL</TableCell>
                  <TableCell className="py-2 text-center text-xs">{totals.totalLeads}</TableCell>
                  <TableCell className="py-2 text-center text-xs tabular-nums">{totals.contacted}</TableCell>
                  <TableCell className="py-2 text-center text-xs tabular-nums text-muted-foreground">{totals.noContact}</TableCell>
                  <TableCell className="py-2 text-center text-xs tabular-nums hidden sm:table-cell">{totals.appointmentsSet}</TableCell>
                  <TableCell className="py-2 text-center text-xs tabular-nums text-muted-foreground hidden sm:table-cell">{totals.noShow}</TableCell>
                  <TableCell className="py-2 text-center text-xs tabular-nums">{totals.sold}</TableCell>
                  <TableCell className="py-2 text-center text-xs tabular-nums text-muted-foreground hidden md:table-cell">{totals.rejected}</TableCell>
                  <TableCell className="py-2 text-center hidden lg:table-cell text-[10px] text-muted-foreground">—</TableCell>
                  <TableCell className="py-2 text-right text-xs hidden sm:table-cell">${totals.revenue.toLocaleString()}</TableCell>
                  <TableCell className="py-2 text-center hidden lg:table-cell">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      {totals.totalLeads > 0 ? `${Math.round((totals.sold / totals.totalLeads) * 100)}%` : "—"}
                    </Badge>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>

      {/* ═══ Appointment Results Trends ═══ */}
      {appointmentResults.length > 0 && (
        <div className="border border-border/60 rounded-sm bg-background p-4">
            <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-3 flex items-center gap-1.5">
              <CalendarCheck className="h-3 w-3" /> Appointment Results Trends (from Recorded Outcomes)
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* By Outcome */}
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground mb-2">By Outcome</p>
                <div className="space-y-1.5">
                  {(["sold", "no_sale", "no_demo", "rescheduled"] as const).map(type => {
                    const count = appointmentResults.filter(r => r.resultType === type).length;
                    const pctVal = appointmentResults.length > 0 ? Math.round((count / appointmentResults.length) * 100) : 0;
                    const colorMap = { sold: "text-success", no_sale: "text-destructive", no_demo: "text-muted-foreground", rescheduled: "text-warning" };
                    const labelMap = { sold: "Sold", no_sale: "No Sale", no_demo: "No Demo", rescheduled: "Rescheduled" };
                    return (
                      <div key={type} className="flex items-center gap-2">
                        <span className="text-[11px] w-24">{labelMap[type]}</span>
                        <Progress value={pctVal} className="flex-1 h-1.5" />
                        <span className={`text-[11px] font-bold w-8 text-right ${colorMap[type]}`}>{count}</span>
                        <span className="text-[10px] text-muted-foreground w-10 text-right">{pctVal}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* By Rep */}
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground mb-2">By Rep</p>
                <div className="space-y-1.5">
                  {getRepPerformanceSummary(appointmentResults, jobs).map(r => (
                    <div key={r.rep} className="flex items-center gap-2">
                      <span className="text-[11px] font-medium w-28 truncate">{r.rep}</span>
                      <span className="text-[10px] text-muted-foreground">{r.total} appts</span>
                      <span className="text-[10px] font-bold text-success ml-auto">{r.closeRate}% close</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* By Source */}
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground mb-2">By Source</p>
                <div className="space-y-1.5">
                  {getSourcePerformanceSummary(appointmentResults, jobs).map(s => (
                    <div key={s.source} className="flex items-center gap-2">
                      <span className="text-[11px] font-medium w-28 truncate">{s.source}</span>
                      <span className="text-[10px] text-muted-foreground">{s.sold}/{s.leads}</span>
                      <span className="text-[10px] font-bold text-primary ml-auto">${s.revenue.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
      )}

      {/* ═══ Preview Dialog ═══ */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" /> Disposition Report Preview
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-xs">
            <div className="border-b pb-3">
              <p className="text-base font-bold">Lead Disposition Report</p>
              <p className="text-muted-foreground">{dateFrom} to {dateTo} · {sourceFilter === "all" ? "All Sources" : sourceFilter}</p>
            </div>

            <div className="grid grid-cols-4 gap-2">
              {[
                { label: "Total Leads", value: totals.totalLeads },
                { label: "Contacted", value: totals.contacted },
                { label: "Sold", value: totals.sold },
                { label: "Revenue", value: `$${totals.revenue.toLocaleString()}` },
              ].map((m) => (
                <div key={m.label} className="rounded-lg border p-2 text-center bg-muted/20">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">{m.label}</p>
                  <p className="text-sm font-bold">{m.value}</p>
                </div>
              ))}
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[9px]">Source</TableHead>
                  <TableHead className="text-[9px] text-center">Leads</TableHead>
                  <TableHead className="text-[9px] text-center">Sold</TableHead>
                  <TableHead className="text-[9px] text-center">Rejected</TableHead>
                  <TableHead className="text-[9px] text-right">Revenue</TableHead>
                  <TableHead className="text-[9px] text-center">Conv%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((d) => (
                  <TableRow key={d.name}>
                    <TableCell className="text-[11px] font-medium">{d.name}</TableCell>
                    <TableCell className="text-[11px] text-center">{d.totalLeads}</TableCell>
                    <TableCell className="text-[11px] text-center font-semibold">{d.sold}</TableCell>
                    <TableCell className="text-[11px] text-center text-destructive">{d.rejected}</TableCell>
                    <TableCell className="text-[11px] text-right">${d.revenue.toLocaleString()}</TableCell>
                    <TableCell className="text-[11px] text-center">{d.conversionRate}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Reason summary in preview */}
            {totalRejections > 0 && (
              <div>
                <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-2">Rejection Reason Breakdown</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {DISPOSITION_REASONS.filter((r) => reasonTotals[r] > 0).map((r) => (
                    <div key={r} className="flex items-center justify-between rounded border p-1.5 bg-muted/10">
                      <span className="text-[10px] font-medium">{r}</span>
                      <span className="text-[10px] font-bold">{reasonTotals[r]} ({Math.round((reasonTotals[r] / totalRejections) * 100)}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t pt-2 text-[10px] text-muted-foreground">
              Generated {new Date().toLocaleDateString()} · CCC OpsFlow Platform
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ Email Report Dialog ═══ */}
      <Dialog open={showEmail} onOpenChange={setShowEmail}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" /> Email Disposition Report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Recipient Email <span className="text-destructive">*</span></Label>
              <Input type="email" placeholder="partner@aggregator.com" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} className="h-9 text-xs" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Subject</Label>
              <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} className="h-9 text-xs" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Additional Notes</Label>
              <Textarea placeholder="Optional message to include with the report..." value={emailNotes} onChange={(e) => setEmailNotes(e.target.value)} className="text-xs min-h-[60px]" rows={3} />
            </div>

            <div className="rounded-lg border bg-muted/20 p-3">
              <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-2">Report Snapshot</p>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div><p className="text-sm font-bold">{totals.totalLeads}</p><p className="text-[8px] text-muted-foreground">Leads</p></div>
                <div><p className="text-sm font-bold text-success">{totals.sold}</p><p className="text-[8px] text-muted-foreground">Sold</p></div>
                <div><p className="text-sm font-bold text-destructive">{totals.rejected}</p><p className="text-[8px] text-muted-foreground">Rejected</p></div>
                <div><p className="text-sm font-bold text-primary">${totals.revenue.toLocaleString()}</p><p className="text-[8px] text-muted-foreground">Revenue</p></div>
              </div>
            </div>

            <div className="rounded-lg border border-dashed border-warning/40 bg-warning/5 p-2.5">
              <p className="text-[10px] text-warning flex items-center gap-1.5">
                <Mail className="h-3 w-3" />
                Email delivery placeholder — will connect to email service when integrated.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowEmail(false)}>Cancel</Button>
              <Button size="sm" className="text-xs gap-1.5" onClick={handleSendEmail}>
                <Send className="h-3.5 w-3.5" /> Send Report
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
